'use client';

import { useMemo } from 'react';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import PersonalInfoSection from './sections/PersonalInfoSection';
import ProfileSection from './sections/ProfileSection';
import ContactSection from './sections/ContactSection';
import LinksSection from './sections/LinksSection';
import SkillsSection from './sections/SkillsSection';
import EmploymentSection from './sections/EmploymentSection';
import EducationSection from './sections/EducationSection';
import CertificationsSection from './sections/CertificationsSection';
import CustomSectionsSection from './sections/CustomSectionsSection';
import ConsentSection from './sections/ConsentSection';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

interface ResumeFormProps {
  /** True when the builder is running without authentication. Threaded down
   *  to the sections that render `ImproveTextControl` so Refine/Improve can
   *  gate on it instead of calling a server-dependent AI endpoint. */
  guestMode?: boolean;
  /** Guest-mode action handler — shows the sign-in dialog (see
   *  `withGuestGate` in `ResumeBuilderClient`). Required when `guestMode`
   *  is true. */
  onGuestAction?: () => void;
  /** Latest analysis report's suggestions, for the inline comment markers. */
  analysisSuggestions?: ResumeAnalysisSuggestion[];
  /** Whether the inline comment markers are currently shown. */
  showAnalysisComments?: boolean;
  /** Applies one suggestion from its inline comment popover. */
  onApplySuggestion?: (suggestion: ResumeAnalysisSuggestion) => void;
  /** id of the suggestion currently being applied, for its loading state. */
  applyingSuggestionId?: string | null;
}

export default function ResumeForm({
  guestMode,
  onGuestAction,
  analysisSuggestions,
  showAnalysisComments,
  onApplySuggestion,
  applyingSuggestionId,
}: ResumeFormProps) {
  const { resume } = useResumeBuilderStore();

  /**
   * Suggestions only surface once the "Annotate" toggle is on, and each one
   * targets exactly one field — sort them by that target once here so every
   * section below can do an O(1) lookup instead of filtering the whole list.
   */
  const { summarySuggestions, employmentMap, customSectionMap } = useMemo(() => {
    const suggestions = showAnalysisComments && analysisSuggestions ? analysisSuggestions : [];
    const summary: ResumeAnalysisSuggestion[] = [];
    const empMap = new Map<string, ResumeAnalysisSuggestion[]>();
    const csMap = new Map<string, ResumeAnalysisSuggestion[]>();
    for (const s of suggestions) {
      switch (s.target.type) {
        case 'summary':
          summary.push(s);
          break;
        case 'employment': {
          const arr = empMap.get(s.target.id) ?? [];
          arr.push(s);
          empMap.set(s.target.id, arr);
          break;
        }
        case 'customSection': {
          const arr = csMap.get(s.target.id) ?? [];
          arr.push(s);
          csMap.set(s.target.id, arr);
          break;
        }
      }
    }
    return { summarySuggestions: summary, employmentMap: empMap, customSectionMap: csMap };
  }, [analysisSuggestions, showAnalysisComments]);

  if (!resume) return null;

  return (
    <div className="p-4">
      <Accordion
        type="multiple"
        defaultValue={['personal-info', 'profile', 'employment']}
        className="space-y-2"
      >
        <AccordionItem value="personal-info" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Personal Information</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <PersonalInfoSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="profile" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Professional Profile</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ProfileSection
              guestMode={guestMode}
              onGuestAction={onGuestAction}
              commentSuggestions={summarySuggestions}
              onApplyCommentSuggestion={onApplySuggestion ?? (() => {})}
              applyingSuggestionId={applyingSuggestionId}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="contact" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Contact Information</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ContactSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="links" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Links</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <LinksSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="employment" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Employment History</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <EmploymentSection
              guestMode={guestMode}
              onGuestAction={onGuestAction}
              commentSuggestionMap={employmentMap}
              onApplyCommentSuggestion={onApplySuggestion ?? (() => {})}
              applyingSuggestionId={applyingSuggestionId}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="education" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Education</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <EducationSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="skills" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Skills</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <SkillsSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="certifications" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Certifications</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <CertificationsSection />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="custom" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Custom Sections</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <CustomSectionsSection
              guestMode={guestMode}
              onGuestAction={onGuestAction}
              commentSuggestionMap={customSectionMap}
              onApplyCommentSuggestion={onApplySuggestion ?? (() => {})}
              applyingSuggestionId={applyingSuggestionId}
            />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="consent" className="rounded-lg border border-border bg-card">
          <AccordionTrigger className="px-4 hover:no-underline">
            <span className="text-label">Consent &amp; Disclosures</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ConsentSection />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
